/**
 * Client-side compression of chat attachments before encryption/upload.
 *
 * Images are downscaled and re-encoded on a canvas (WebP where the engine
 * supports encoding it, otherwise JPEG/PNG). Videos are transcoded to H.264
 * MP4 via mediabunny (WebCodecs), capped to 720p-class resolution. Both paths
 * keep whichever of original/compressed is smaller, and every failure mode
 * falls back to the original file — compression is best-effort, never a
 * reason an attachment can't be sent.
 */

// Images smaller than this are sent as-is (recompression gains are marginal).
const IMAGE_SKIP_BYTES = 400 * 1024;
// Long-edge cap for images.
const IMAGE_MAX_DIMENSION = 2048;
const IMAGE_QUALITY = 0.82;

// Videos smaller than this are sent as-is.
const VIDEO_SKIP_BYTES = 8 * 1024 * 1024;
// Long-edge cap for videos (720p-class).
const VIDEO_MAX_DIMENSION = 1280;

// Formats we never touch: GIFs may be animated (canvas would flatten them to
// one frame) and SVGs are already tiny and lossless.
const IMAGE_SKIP_MIMES = ['image/gif', 'image/svg+xml'];
// Only these inputs can carry transparency worth preserving.
const ALPHA_MIMES = ['image/png', 'image/webp', 'image/avif'];

function replaceExtension(name, ext) {
  const base = name.includes('.') ? name.slice(0, name.lastIndexOf('.')) : name;
  return `${base}.${ext}`;
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

// Any fully/partially transparent pixel means we must keep an alpha-capable
// output format. Sampled every 4th pixel — plenty for a "has alpha?" answer.
function hasTransparency(ctx, width, height) {
  const data = ctx.getImageData(0, 0, width, height).data;
  for (let i = 3; i < data.length; i += 16) {
    if (data[i] < 255) return true;
  }
  return false;
}

/**
 * Encode an already-sized canvas to the smallest sensible still-image File,
 * reusing the attachment pipeline's WebP→PNG/JPEG fallback + alpha detection.
 * Unlike `compressImage`, there's no size-skip or downscale here — the caller
 * (the avatar/logo cropper) has already produced the exact output dimensions,
 * so this only picks the best format/quality. WebP (alpha + best ratio) when
 * the engine can encode it; otherwise PNG for transparent, JPEG for opaque.
 *
 * @param {HTMLCanvasElement} canvas
 * @param {string} [name] base filename (extension is set from the chosen type)
 * @param {number} [quality]
 * @returns {Promise<File>}
 */
export async function encodeCanvasToImageFile(canvas, name = 'image', quality = IMAGE_QUALITY) {
  const ctx = canvas.getContext('2d');
  const keepAlpha = hasTransparency(ctx, canvas.width, canvas.height);

  let blob = await canvasToBlob(canvas, 'image/webp', quality);
  if (blob && blob.type !== 'image/webp') {
    blob = keepAlpha
      ? await canvasToBlob(canvas, 'image/png')
      : await canvasToBlob(canvas, 'image/jpeg', quality);
  }
  if (!blob) blob = await canvasToBlob(canvas, 'image/png');

  const ext = (blob.type && blob.type.split('/')[1]) || 'png';
  return new File([blob], replaceExtension(name, ext), { type: blob.type || 'image/png' });
}

async function compressImage(file, { signal } = {}) {
  if (file.size < IMAGE_SKIP_BYTES) return file;
  if (IMAGE_SKIP_MIMES.includes(file.type)) return file;
  if (typeof createImageBitmap !== 'function') return file;

  // createImageBitmap applies EXIF orientation, so photos stay upright.
  const bitmap = await createImageBitmap(file);
  try {
    if (signal?.aborted) return file;
    const scale = Math.min(1, IMAGE_MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, width, height);

    const keepAlpha = ALPHA_MIMES.includes(file.type) && hasTransparency(ctx, width, height);

    // Prefer WebP (alpha + best ratio). Engines without a WebP encoder
    // (WKWebView) silently return a PNG; detect that via blob.type and fall
    // back to PNG for transparent images or JPEG for opaque ones.
    let blob = await canvasToBlob(canvas, 'image/webp', IMAGE_QUALITY);
    if (blob && blob.type !== 'image/webp') {
      blob = keepAlpha
        ? await canvasToBlob(canvas, 'image/png')
        : await canvasToBlob(canvas, 'image/jpeg', IMAGE_QUALITY);
    }
    if (!blob || blob.size >= file.size) return file;

    const ext = blob.type.split('/')[1];
    return new File([blob], replaceExtension(file.name || 'image', ext), { type: blob.type });
  } finally {
    bitmap.close();
  }
}

async function compressVideo(file, { signal, onProgress } = {}) {
  if (file.size < VIDEO_SKIP_BYTES) return file;
  // WebCodecs is required (present in WebView2/Chromium and WKWebView 16.4+).
  if (typeof window === 'undefined' || typeof window.VideoEncoder !== 'function') return file;

  const {
    Input,
    Output,
    Conversion,
    BlobSource,
    BufferTarget,
    Mp4OutputFormat,
    ALL_FORMATS,
    QUALITY_MEDIUM,
    canEncodeVideo,
  } = await import('mediabunny');

  if (!(await canEncodeVideo('avc'))) return file;

  const input = new Input({ source: new BlobSource(file), formats: ALL_FORMATS });
  const output = new Output({ format: new Mp4OutputFormat(), target: new BufferTarget() });

  const conversion = await Conversion.init({
    input,
    output,
    video: (track) => {
      const scale = Math.min(1, VIDEO_MAX_DIMENSION / Math.max(track.displayWidth, track.displayHeight));
      return {
        // H.264 wants even dimensions; height follows from aspect ratio.
        width: Math.max(2, Math.round((track.displayWidth * scale) / 2) * 2),
        codec: 'avc',
        bitrate: QUALITY_MEDIUM,
        // Without this, mp4-compatible inputs are copied verbatim and never
        // get smaller.
        forceTranscode: true,
      };
    },
    // Audio is left to the default: copied when mp4-compatible, transcoded
    // otherwise — audio is rarely the size problem.
    showWarnings: false,
  });

  // A video track the engine can't handle means the output would be
  // audio-only or empty — send the original instead.
  if (!conversion.isValid || conversion.discardedTracks.some((t) => t.track.type === 'video')) {
    return file;
  }

  if (onProgress) conversion.onProgress = (progress) => onProgress(progress);
  if (signal) {
    if (signal.aborted) return file;
    signal.addEventListener('abort', () => conversion.cancel(), { once: true });
  }

  await conversion.execute();

  const buffer = output.target.buffer;
  if (!buffer || buffer.byteLength >= file.size) return file;
  return new File([buffer], replaceExtension(file.name || 'video', 'mp4'), { type: 'video/mp4' });
}

/**
 * Compress an attachment when it's an image or video; anything else (and any
 * failure, including an aborted video transcode) resolves to the original
 * file.
 *
 * @param {File} file
 * @param {{ signal?: AbortSignal, onProgress?: (fraction: number) => void }} [opts]
 * @returns {Promise<File>}
 */
export async function prepareAttachmentFile(file, opts = {}) {
  const mime = file?.type || '';
  try {
    if (mime.startsWith('image/')) return await compressImage(file, opts);
    if (mime.startsWith('video/')) return await compressVideo(file, opts);
  } catch (err) {
    console.warn('[chat] attachment compression failed, sending original:', err);
  }
  return file;
}
